/**
 * AgentCPanelContent — Embeddable Agent C chat interface.
 * Designed to live inside DesktopRightPanel / RightDrawerShell.
 * NOT a floating panel — just the content (header is handled by the shell).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Lightbulb, ArrowRight, Loader2, Sparkles, Shield, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface AgentCContext {
  type: string;
  label?: string;
  params?: Record<string, unknown>;
}

export interface AgentCPanelContentProps {
  role: 'admin' | 'brand' | 'creator' | 'customer';
  supabaseUrl: string;
  accessToken: string | null;
  suggestedPrompts?: string[];
  context?: AgentCContext | null;
}

interface AgentCResponse {
  answer: string;
  insights: string[];
  suggestedActions: string[];
  dataSource: string;
  confidence?: 'low' | 'medium' | 'high';
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  response?: AgentCResponse;
}

const confidenceConfig = {
  low: { label: 'Low', icon: AlertTriangle, className: 'text-orange-500 bg-orange-500/10' },
  medium: { label: 'Medium', icon: Shield, className: 'text-yellow-500 bg-yellow-500/10' },
  high: { label: 'High', icon: CheckCircle2, className: 'text-green-500 bg-green-500/10' },
};

export const AgentCPanelContent: React.FC<AgentCPanelContentProps> = ({
  role,
  supabaseUrl,
  accessToken,
  suggestedPrompts = [],
  context,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const toggleExpand = (index: number) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const sendQuery = useCallback(async (query: string) => {
    if (!query.trim() || !accessToken) return;
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content: query }]);
    setInput('');
    setLoading(true);

    try {
      const body: Record<string, unknown> = { role, query };
      if (context?.type) {
        body.contextType = context.type;
        body.params = context.params || {};
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/copilot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `Error ${res.status}`);
      }

      const data: AgentCResponse = await res.json();
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.answer, response: data },
      ]);
    } catch (e: any) {
      setError(e.message);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Error: ${e.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [role, supabaseUrl, accessToken, context]);

  const renderMessage = (msg: Message, i: number) => (
    <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
      <div className={cn(
        'rounded-xl px-4 py-3',
        msg.role === 'user'
          ? 'max-w-[85%] bg-primary text-primary-foreground text-sm'
          : 'max-w-[95%] bg-muted text-foreground text-base',
      )} style={msg.role === 'assistant' ? { lineHeight: '1.65' } : undefined}>
        <p className="whitespace-pre-wrap">{msg.content}</p>

        {msg.response?.confidence && (() => {
          const conf = confidenceConfig[msg.response.confidence];
          const Icon = conf.icon;
          return (
            <span className={cn('inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-2', conf.className)}>
              <Icon className="h-3 w-3" /> {conf.label}
            </span>
          );
        })()}

        {msg.response && (msg.response.insights.length > 0 || msg.response.suggestedActions.length > 0) && (
          <button onClick={() => toggleExpand(i)} className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline">
            {expandedMessages.has(i) ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expandedMessages.has(i) ? 'Less' : 'Details'}
          </button>
        )}

        {msg.response && expandedMessages.has(i) && (
          <>
            {msg.response.insights.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                <p className="font-semibold text-xs uppercase tracking-wide opacity-70">Why</p>
                {msg.response.insights.map((ins, j) => (
                  <p key={j} className="flex items-start gap-1.5 text-sm" style={{ lineHeight: '1.5' }}>
                    <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> <span>{ins}</span>
                  </p>
                ))}
              </div>
            )}
            {msg.response.suggestedActions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                <p className="font-semibold text-xs uppercase tracking-wide opacity-70">Actions</p>
                {msg.response.suggestedActions.map((act, j) => (
                  <p key={j} className="flex items-start gap-1.5 text-sm" style={{ lineHeight: '1.5' }}>
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> <span>{act}</span>
                  </p>
                ))}
              </div>
            )}
          </>
        )}

        {msg.response?.dataSource && (
          <p className="mt-2 text-xs opacity-50 italic">Data: {msg.response.dataSource}</p>
        )}

        {msg.role === 'assistant' && msg.response && (
          <div className="mt-2 flex items-center gap-3">
            <button className="text-xs text-muted-foreground hover:text-green-600 flex items-center gap-1" title="Good">
              <ThumbsUp className="h-3.5 w-3.5" />
            </button>
            <button className="text-xs text-muted-foreground hover:text-red-600 flex items-center gap-1" title="Bad">
              <ThumbsDown className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Scrollable messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.length === 0 ? (
          <div className="space-y-4">
            {context?.label && (
              <div className="px-3 py-2 rounded-lg bg-primary/5 border border-primary/10">
                <p className="text-xs font-medium text-primary">Context: {context.label}</p>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-5 w-5 text-primary shrink-0" />
              <span>Ask about your data. Answers are explainable and data-backed.</span>
            </div>
            {suggestedPrompts.map((p, i) => (
              <button
                key={i}
                onClick={() => sendQuery(p)}
                className="w-full text-left text-sm px-4 py-3 rounded-xl border border-border hover:bg-muted transition-colors"
              >
                <Lightbulb className="inline h-4 w-4 mr-2 text-primary" />
                {p}
              </button>
            ))}
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing…
          </div>
        )}
      </div>

      {/* Input bar — font-size ≥16px prevents iOS auto-zoom on focus */}
      <div
        className="shrink-0 border-t border-border px-4 pt-3 bg-background"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)' }}
      >
        {error && <p className="text-xs text-destructive mb-2">{error}</p>}
        <form onSubmit={(e) => { e.preventDefault(); sendQuery(input); }} className="flex gap-2 items-center">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendQuery(input);
              }
            }}
            placeholder="Ask about your data…"
            rows={1}
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2.5 text-base leading-snug focus:outline-none focus:ring-1 focus:ring-primary resize-none max-h-[120px] overflow-y-auto"
            disabled={loading}
            style={{ fontSize: '16px' }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="shrink-0 h-10 w-10 rounded-lg bg-primary flex items-center justify-center text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

AgentCPanelContent.displayName = 'AgentCPanelContent';
